/**
 * PortalBrandHeader — the tenant-branded contents of a public portal's banner
 *
 * Rendered through PortalLayout's `header` slot so it lands OUTSIDE <main> and
 * the surrounding <header> actually resolves to role="banner" (issue #2197).
 *
 * Shared by both hosted-form renderers (SubmitNdfForm and the legacy
 * SubmitForm) so the two can never drift on what a public form's banner shows.
 *
 * `GET /public/form/:publicUrl` always returns `branding.companyName` (it falls
 * back to the tenant's own name), so in practice the banner is never empty; the
 * Respectly wordmark is the fallback for the states that render before — or
 * without — form data, such as the error view.
 */
export interface PortalBrandHeaderProps {
  logoUrl?: string | null;
  companyName?: string | null;
}

export function PortalBrandHeader({ logoUrl, companyName }: PortalBrandHeaderProps) {
  if (!logoUrl && !companyName) {
    return (
      <div className="flex items-center">
        <img src="/logo-dark.svg" alt="Respectly" className="h-8 w-auto" />
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-3">
      {logoUrl && (
        <img
          src={logoUrl}
          // Decorative when the name is already visible beside it — otherwise a
          // screen reader announces the same company twice.
          alt={companyName ? '' : 'Company logo'}
          className="h-8 w-auto object-contain"
        />
      )}
      {companyName && (
        <span className="truncate text-sm font-medium text-slate-700">{companyName}</span>
      )}
    </div>
  );
}

export default PortalBrandHeader;
